import { useState, useRef, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { X, Image as ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { type LogoPathArgs } from "@/lib/slugify";
import { getLogoPublicUrl } from "@/lib/branding";

interface LogoUploadProps {
  pathArgs?: LogoPathArgs | null;
  onUploaded: (storagePath: string) => void;
  onFileSelected?: (file: File) => void;
  currentLogoUrl?: string | null;
}

const MAX_SIZE = 2 * 1024 * 1024; // 2MB
const ACCEPTED = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"];

/**
 * Call the upload-logo edge function instead of direct storage upload.
 * Returns the relative storage path on success.
 */
async function callUploadLogoEdgeFn(
  file: File,
  pathArgs: LogoPathArgs,
): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("level", pathArgs.level);
  formData.append("client_name", pathArgs.clientName);
  formData.append("entity_id", pathArgs.entityId);

  if (pathArgs.level === "sub_org" || pathArgs.level === "portfolio") {
    formData.append("sub_org_name", pathArgs.subOrgName);
  }
  if (pathArgs.level === "portfolio") {
    formData.append("company_name", pathArgs.companyName);
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error("Not authenticated");

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-logo`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json.error || json.detail || "Upload failed");
  }

  console.log("[LogoUpload] edge fn returned storagePath:", json.storagePath);
  return json.storagePath as string;
}

export function LogoUpload({ pathArgs, onUploaded, onFileSelected, currentLogoUrl }: LogoUploadProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedPath, setUploadedPath] = useState<string | null>(currentLogoUrl ?? null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFile = useCallback(
    async (file: File) => {
      if (!ACCEPTED.includes(file.type)) {
        toast({ title: "Invalid file type", description: "Use PNG, JPG, SVG, or WebP.", variant: "destructive" });
        return;
      }
      if (file.size > MAX_SIZE) {
        toast({ title: "File too large", description: "Maximum 2 MB.", variant: "destructive" });
        return;
      }

      // Show local preview
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(file);

      // If pathArgs not yet available, defer upload
      if (!pathArgs) {
        onFileSelected?.(file);
        toast({ title: "Logo selected", description: "Will be uploaded when the entity is saved." });
        return;
      }

      await uploadFile(file, pathArgs);
    },
    [pathArgs, onUploaded, onFileSelected, toast],
  );

  const uploadFile = async (file: File, args: LogoPathArgs) => {
    setUploading(true);
    try {
      const storagePath = await callUploadLogoEdgeFn(file, args);
      setUploadedPath(storagePath);
      onUploaded(storagePath);
      toast({ title: "Logo uploaded" });
    } catch (err: any) {
      console.error("[LogoUpload] upload error:", err);
      toast({ title: "Upload failed", description: err?.message ?? "Unknown error", variant: "destructive" });
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const clear = () => {
    setPreview(null);
    setUploadedPath(null);
    onUploaded("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const displayUrl = preview || (uploadedPath ? getLogoPublicUrl(uploadedPath) : null);

  return (
    <div className="space-y-2">
      <Label>Logo (optional)</Label>

      {displayUrl ? (
        <div className="relative w-24 h-24 rounded-md border border-border overflow-hidden bg-muted">
          <img src={displayUrl} alt="Logo preview" className="w-full h-full object-contain" />
          <button
            type="button"
            onClick={clear}
            className="absolute top-1 right-1 rounded-full bg-background/80 p-0.5 hover:bg-destructive/20"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          className="flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-border p-4 cursor-pointer hover:border-primary/50 transition-colors"
        >
          {uploading ? (
            <p className="text-xs text-muted-foreground">Uploading…</p>
          ) : (
            <>
              <ImageIcon className="h-6 w-6 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Drop logo or click to browse</p>
              <p className="text-[10px] text-muted-foreground">PNG, JPG, SVG · max 2 MB</p>
            </>
          )}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
    </div>
  );
}

/**
 * Upload a logo file via the upload-logo edge function.
 * Call this after entity creation when the entity ID is known.
 * Returns the relative storage path.
 */
export async function uploadLogoForEntity(
  file: File,
  pathArgs: LogoPathArgs,
): Promise<string> {
  console.log("[uploadLogoForEntity] calling edge fn, level:", pathArgs.level);
  return callUploadLogoEdgeFn(file, pathArgs);
}
